// src/features/analytics/AnalyticsPage.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import API from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';
import MathText from '../../components/MathText';

const autoFormatMath = (text) => {
  if (!text) return '';
  if (text.includes('$')) return text; 
  return text
    .replace(/\b(\d+)\/(\d+)\b/g, '$\\frac{$1}{$2}$') 
    .replace(/([a-zA-Zа-яА-Я])\^(\d+)/g, '$$$1^{$2}$$') 
    .replace(/>=/g, '$\\ge$')                        
    .replace(/<=/g, '$\\le$')                        
    .replace(/!=/g, '$\\neq$');                      
};

const formatLastSeen = (lastLoginStr) => {
  if (!lastLoginStr) return "Ни разу не заходил";
  const lastLogin = new Date(lastLoginStr);
  const now = new Date();
  const diffMs = now - lastLogin;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "Только что вышел";
  if (diffMins < 60) return `${diffMins} мин. назад`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} ч. назад`;
  return lastLogin.toLocaleDateString();
};

// ============================================================================
// ЕДИНЫЙ КОМПОНЕНТ ФИЛЬТРА EXCEL-STYLE (С УМНЫМ ПОИСКОМ И ОЧИСТКОЙ)
// ============================================================================
const ExcelCategoryFilter = ({ label, options, selectedValues, onChange, onSort, columnKey }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const searchLower = search.toLowerCase().trim();
  const normalizedOptions = options.map(opt => typeof opt === 'object' && opt !== null ? opt : { value: opt, label: opt });
  const filteredOptions = normalizedOptions.filter(opt => String(opt.label).toLowerCase().includes(searchLower));

  const hasExactMatch = normalizedOptions.some(opt => String(opt.label).toLowerCase() === searchLower);
  if (searchLower && !hasExactMatch) {
    filteredOptions.push({ value: search, label: `Искать: "${search}"` });
  }

  const isAllSelected = selectedValues.length === 0;

  const handleToggleAll = () => { onChange(columnKey, []); };

  const handleToggleOption = (optValue) => {
    let current = [...selectedValues];
    if (isAllSelected) {
      current = normalizedOptions.map(o => o.value).filter(v => v !== optValue);
    } else {
      if (current.includes(optValue)) {
        current = current.filter(v => v !== optValue);
      } else {
        current.push(optValue);
      }
    }
    if (current.length === normalizedOptions.length) current = []; 
    onChange(columnKey, current);
  };

  const handleClearFilter = () => {
    onChange(columnKey, []);
    setSearch('');
    setIsOpen(false);
  };

  return (
    <div className="relative w-full" ref={wrapperRef}>
      <button onClick={() => setIsOpen(!isOpen)} className={`w-full flex items-center justify-between bg-white border ${!isAllSelected ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-gray-300'} text-gray-700 rounded-xl px-3 py-2 text-[11px] font-bold outline-none cursor-pointer transition-colors shadow-sm`}>
        <span className="truncate mr-2 text-left">{label}</span>
        {!isAllSelected ? (
          <svg className="w-3 h-3 shrink-0 text-blue-600" fill="currentColor" viewBox="0 0 20 20"><path d="M5 4a1 1 0 00-2 0v7.268a2 2 0 000 3.464V16a1 1 0 102 0v-1.268a2 2 0 000-3.464V4zM11 4a1 1 0 10-2 0v1.268a2 2 0 000 3.464V16a1 1 0 102 0V8.732a2 2 0 000-3.464V4zM16 3a1 1 0 011 1v7.268a2 2 0 010 3.464V16a1 1 0 11-2 0v-1.268a2 2 0 010-3.464V4a1 1 0 011-1z" /></svg>
        ) : (
          <svg className={`w-3 h-3 shrink-0 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
        )}
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-64 bg-white border border-gray-200 shadow-2xl rounded-xl p-2 text-xs text-gray-700 top-full left-0">
          <button onClick={() => { onSort(columnKey, 'asc'); setIsOpen(false); }} className="w-full text-left px-2 py-1.5 hover:bg-gray-100 rounded flex items-center gap-2 font-bold text-gray-600 transition-colors">
            <span className="text-blue-500">↓</span> От А до Я
          </button>
          <button onClick={() => { onSort(columnKey, 'desc'); setIsOpen(false); }} className="w-full text-left px-2 py-1.5 hover:bg-gray-100 rounded flex items-center gap-2 font-bold text-gray-600 transition-colors mt-0.5">
            <span className="text-blue-500">↑</span> От Я до А
          </button>

          <button onClick={handleClearFilter} disabled={isAllSelected} className={`w-full text-left px-2 py-1.5 rounded flex items-center gap-2 font-bold transition-colors mt-0.5 ${isAllSelected ? 'text-gray-300 cursor-not-allowed' : 'text-red-500 hover:bg-red-50'}`}>
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"></path></svg>
            Очистить фильтр
          </button>

          <div className="border-t border-gray-100 my-2"></div>
          <input type="text" placeholder="Поиск по списку/базе..." className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 outline-none focus:border-blue-500 mb-2 shadow-inner" value={search} onChange={e => setSearch(e.target.value)} />

          <div className="max-h-48 overflow-y-auto custom-scrollbar space-y-0.5 pr-1">
            <label className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer font-bold text-gray-600">
              <input type="checkbox" checked={isAllSelected} onChange={handleToggleAll} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer w-3.5 h-3.5" />
              <span>(Выделить все)</span>
            </label>
            {filteredOptions.map((opt, idx) => (
              <label key={idx} className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer font-medium text-gray-700">
                <input type="checkbox" checked={isAllSelected || selectedValues.includes(opt.value)} onChange={() => handleToggleOption(opt.value)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer w-3.5 h-3.5 shrink-0" />
                <span className="truncate" title={opt.label}>{opt.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// ОСНОВНАЯ СТРАНИЦА АНАЛИТИКИ
// ============================================================================
const AnalyticsPage = () => {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabParam || 'users'); 
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [siteCounters, setSiteCounters] = useState({ views: 0, logins: 0 }); 
  const navigate = useNavigate();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ first_name: '', last_name: '', role: '', is_active: true });
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (tabParam) setActiveTab(tabParam);
  }, [tabParam]);

  const fetchAnalyticsData = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const response = await API.get('/api/questions-stats/');
      setData(response.data);
    } catch (error) {
      console.error('Ошибка загрузки аналитики:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const fetchSiteCounters = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await API.get('/api/hit-homepage/');
      setSiteCounters({ views: res.data.views || 0, logins: res.data.logins || 0 });
    } catch (err) {}
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) { fetchAnalyticsData(); fetchSiteCounters(); } 
    else { setLoading(false); }
  }, [isAuthenticated, fetchAnalyticsData, fetchSiteCounters]);

  const handleToggleActive = async (userId) => {
    try {
      await API.post(`/api/users-admin/${userId}/toggle_active/`, {});
      fetchAnalyticsData();
    } catch (err) { alert("Не удалось изменить статус пользователя"); }
  };

  const handleOpenEditModal = (user) => {
    setEditingUser(user);
    setEditForm({ first_name: user.first_name || '', last_name: user.last_name || '', role: user.role || 'student', is_active: user.is_active });
    setIsEditModalOpen(true);
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    try {
      await API.patch(`/api/users-admin/${editingUser.id}/`, editForm);
      setIsEditModalOpen(false);
      fetchAnalyticsData();
    } catch (err) { alert("Ошибка обновления пользователя"); }
  };

  if (loading) return <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50"><div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div></div>;

  if (!isAuthenticated || !data) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4">
      <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100 max-w-sm w-full text-center space-y-4">
        <span className="text-4xl">🔒</span>
        <h2 className="text-lg font-black text-gray-900">Ошибка доступа к данным</h2>
        <button onClick={() => navigate('/login')} className="w-full py-3 bg-blue-600 text-white font-bold text-xs rounded-xl uppercase tracking-wider">Авторизоваться</button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 font-sans antialiased">
        {activeTab === 'users' && (
          <div className="max-w-[1600px] mx-auto space-y-8">
            <h1 className="text-2xl font-black text-gray-900">📊 ПАНЕЛЬ СУПЕР-АДМИНИСТРАТОРА</h1>
             <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
               <StatCardWithIndicator label="Общие просмотры" primaryVal={siteCounters.views} secondaryVal="Всего" color="blue" />
               <StatCardWithIndicator label="Всего авторизаций" primaryVal={siteCounters.logins} secondaryVal="Сессий" color="green" />
               <StatCardWithIndicator label="Зарегистрировано" primaryVal={data.total_users_count} secondaryVal={`${data.active_users_count} активны`} color="purple" />
               <StatCardWithIndicator label="Банк вопросов" primaryVal={data.total_questions_count} secondaryVal={`${data.public_questions_count} паблик`} color="orange" />
             </div>
             <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
               <div className="lg:col-span-2 bg-white rounded-[2.5rem] border border-gray-100 shadow-sm p-8 space-y-6">
                 <h3 className="text-base font-black text-gray-900 tracking-tight flex items-center gap-2">👥 Реестр пользователей</h3>
                 <div className="overflow-x-auto">
                   <table className="w-full text-left border-collapse">
                     <thead>
                       <tr className="border-b border-gray-100">
                         <th className="pb-3 text-[10px] font-black text-gray-300 uppercase tracking-widest">Пользователь</th>
                         <th className="pb-3 text-[10px] font-black text-gray-300 uppercase tracking-widest">Роль</th>
                         <th className="pb-3 text-[10px] font-black text-gray-300 uppercase tracking-widest">Последний визит</th>
                         <th className="pb-3 text-[10px] font-black text-gray-300 uppercase tracking-widest">Статус</th>
                         <th className="pb-3 text-[10px] font-black text-gray-300 uppercase tracking-widest text-right">Действия</th>
                       </tr>
                     </thead>
                     <tbody className="divide-y divide-gray-50">
                       {data.users && data.users.map(user => (
                         <tr key={user.id} className="group hover:bg-gray-50/40 transition-all">
                           <td className="py-4"><div className="font-bold text-xs text-gray-800">{user.username}</div><div className="text-[10px] text-gray-400 font-medium">{user.first_name} {user.last_name}</div></td>
                           <td className="py-4"><span className={`px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider ${user.role?.toLowerCase() === 'admin' ? 'bg-red-50 text-red-600 border border-red-100' : user.role?.toLowerCase() === 'teacher' ? 'bg-blue-50 text-blue-600 border border-blue-100' : 'bg-gray-100 text-gray-500'}`}>{user.role}</span></td>
                           <td className="py-4 text-[10px] font-bold text-gray-400">{formatLastSeen(user.last_login)}</td>
                           <td className="py-4"><span className={`text-[10px] font-bold ${user.is_active ? 'text-green-500' : 'text-red-400'}`}>{user.is_active ? '● Активен' : '○ Блок'}</span></td>
                           <td className="py-4 text-right space-x-1 whitespace-nowrap">
                             <button onClick={() => handleOpenEditModal(user)} className="px-2.5 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-[10px] font-black rounded-lg transition-all">✏️ Править</button>
                             <button onClick={() => handleToggleActive(user.id)} className={`px-2.5 py-1.5 text-[10px] font-black rounded-lg transition-all ${user.is_active ? 'bg-red-50 text-red-600 hover:bg-red-100' : 'bg-green-50 text-green-600 hover:bg-green-100'}`}>{user.is_active ? 'Заблочить' : 'Разблочить'}</button>
                           </td>
                         </tr>
                       ))}
                     </tbody>
                   </table>
                 </div>
               </div>
               <div className="space-y-6">
                 <div className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm p-8 space-y-4">
                   <h3 className="text-base font-black text-gray-900 tracking-tight">📚 Вопросы по предметам</h3>
                   <div className="space-y-2">
                     {data.questions_by_subject && data.questions_by_subject.map((item, idx) => (
                       <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-2xl"><span className="text-xs font-bold text-gray-600">{item.subject__name || "Без названия"}</span><span className="px-2.5 py-1 bg-white border border-gray-100 rounded-xl text-xs font-black text-gray-800 shadow-sm">{item.count}</span></div>
                     ))}
                   </div>
                 </div>
               </div>
             </div>
          </div>
        )}

        {activeTab === 'questions' && (
          <div className="max-w-[1600px] mx-auto">
            <QuestionsBankContent users={data?.users || []} />
          </div>
        )}

      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-[2.5rem] border border-gray-100 max-w-md w-full p-8 shadow-2xl space-y-6">
            <h3 className="text-base font-black text-gray-900 tracking-tight">Редактирование профиля: {editingUser?.username}</h3>
            <form onSubmit={handleSaveUser} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Имя</label><input type="text" className="w-full px-3 py-2 border rounded-xl text-xs font-semibold" value={editForm.first_name} onChange={(e) => setEditForm({...editForm, first_name: e.target.value})} /></div>
                <div><label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Фамилия</label><input type="text" className="w-full px-3 py-2 border rounded-xl text-xs font-semibold" value={editForm.last_name} onChange={(e) => setEditForm({...editForm, last_name: e.target.value})} /></div>
              </div>
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Глобальная роль системы</label>
                <select className="w-full px-3 py-2 border rounded-xl text-xs font-bold text-gray-700 bg-white" value={editForm.role} onChange={(e) => setEditForm({...editForm, role: e.target.value})}>
                  <option value="student">Student (Ученик)</option><option value="teacher">Teacher (Преподаватель)</option><option value="admin">Admin (Администратор)</option>
                </select>
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button type="button" onClick={() => setIsEditModalOpen(false)} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-black rounded-xl">Отмена</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-black rounded-xl shadow-md">Сохранить</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const StatCardWithIndicator = ({ label, primaryVal, secondaryVal, color }) => {
  const colorMap = { blue: "text-blue-600 bg-blue-50/40 border-blue-100", green: "text-green-600 bg-green-50/40 border-green-100", purple: "text-purple-600 bg-purple-50/40 border-purple-100", orange: "text-orange-600 bg-orange-50/40 border-orange-100" };
  const textColors = { blue: "text-blue-600", green: "text-green-600", purple: "text-purple-600", orange: "text-orange-500" };

  return (
    <div className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm flex flex-col justify-between">
      <p className="text-[10px] font-black text-gray-300 uppercase tracking-widest mb-3">{label}</p>
      <div className="flex items-baseline justify-between gap-2">
        <p className={`text-4xl font-black tracking-tighter ${textColors[color]}`}>{primaryVal}</p>
        <div className={`px-3 py-1 rounded-xl text-xs font-black border uppercase tracking-wider ${colorMap[color]}`}>{secondaryVal}</div>
      </div>
    </div>
  );
};

// ============================================================================
// БАНК ВОПРОСОВ (С СЕРВЕРНОЙ ПАГИНАЦИЕЙ, EXCEL-ФИЛЬТРАМИ И МАССОВЫМИ ДЕЙСТВИЯМИ)
// ============================================================================
function QuestionsBankContent({ users }) {
  const [questions, setQuestions] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // МАССОВЫЕ ДЕЙСТВИЯ
  const [selectedIds, setSelectedIds] = useState([]);

  const [filters, setFilters] = useState({
    id: [], text: [], class_level: [], subject: [], difficulty: [], topic: [], author: [], status: []
  });
  const [ordering, setOrdering] = useState('-id'); 

  const fetchBankData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        page_size: 30, 
        ordering: ordering,
        id: filters.id.length ? filters.id.join(',') : undefined,
        search: filters.text.length ? filters.text.join(' ') : undefined, 
        class_level: filters.class_level.length ? filters.class_level.join(',') : undefined,
        subject: filters.subject.length ? filters.subject.join(',') : undefined,
        difficulty: filters.difficulty.length ? filters.difficulty.join(',') : undefined,
        topic: filters.topic.length ? filters.topic.join(',') : undefined,
        creator: filters.author.length ? filters.author.join(',') : undefined,
        is_public: filters.status.length === 1 ? (filters.status[0] === 'public') : undefined
      };

      const [qRes, sRes, tRes] = await Promise.all([
        API.get('/api/questions/', { params }),
        API.get('/api/subjects/'),
        API.get('/api/topics/')
      ]);

      if (qRes.data && qRes.data.results) {
        setQuestions(qRes.data.results);
        setTotalPages(qRes.data.total_pages || Math.ceil(qRes.data.count / 30) || 1);
        setTotalCount(qRes.data.count || 0);
      } else {
        const all = Array.isArray(qRes.data) ? qRes.data : [];
        setQuestions(all);
        setTotalCount(all.length);
        setTotalPages(1);
      }

      setSubjects(sRes.data);
      setTopics(tRes.data);
    } catch (err) {
      console.error("Ошибка загрузки данных банка:", err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, filters, ordering]);

  useEffect(() => { fetchBankData(); }, [fetchBankData]);

  // Сброс выделения при переключении страниц или фильтров
  useEffect(() => {
    setSelectedIds([]);
  }, [currentPage, filters, ordering]);

  useEffect(() => { if (window.MathJax && !loading) window.MathJax.typesetPromise().catch(() => {}); });

  const getSubjName = (id) => subjects.find(s => s.id === id)?.name || '—';
  const getTopicName = (id) => topics.find(t => t.id === id)?.title || '—';
  const getAuthorName = (q) => {
    const userMatch = users.find(u => u.id === q.creator);
    return q.creator_username || userMatch?.username || userMatch?.first_name || `ID: ${q.creator}`;
  };
  const getTruncText = (q) => q.text ? q.text.substring(0, 50) + '...' : 'Без текста (только фото)';

  // Функция для группировки опций и переименования пустых строк в "Пустой"
  const groupOptions = (items) => {
    const map = new Map();
    items.forEach(item => {
        const label = String(item.label || '').trim() || 'Пустой';
        if (!map.has(label)) map.set(label, []);
        if (item.value !== null && item.value !== undefined && item.value !== '') {
           map.get(label).push(String(item.value));
        }
    });

    return Array.from(map.entries()).map(([label, vals]) => ({
        label,
        value: Array.from(new Set(vals)).join(',')
    })).sort((a, b) => a.label.localeCompare(b.label));
  };

  // Опции для выпадающих списков
  const rawIds = questions.map(q => ({ value: q.id, label: q.id }));
  const idOptions = groupOptions(rawIds).slice(0, 20); 
  
  const rawTexts = questions.map(q => ({ value: getTruncText(q), label: getTruncText(q) }));
  const textOptions = groupOptions(rawTexts).slice(0, 20); 
  
  const classOptions = groupOptions([5, 6, 7, 8, 9, 10, 11].map(c => ({ value: c, label: `${c} класс` })));
  const subjectOptions = groupOptions(subjects.map(s => ({ value: s.id, label: s.name })));
  const topicOptions = groupOptions(topics.map(t => ({ value: t.id, label: t.title })));
  const difficultyOptions = groupOptions(['Базовый', 'Повышенный', 'Высокий'].map(d => ({ value: d, label: d })));
  const authorOptions = groupOptions(users.map(u => ({ value: u.id, label: u.username || u.first_name || `ID: ${u.id}` })));
  const statusOptions = groupOptions([{ value: 'public', label: 'Общий' }, { value: 'private', label: 'Личный' }]);

  const handleFilterChange = (key, val) => {
    setFilters(prev => ({ ...prev, [key]: val }));
    setCurrentPage(1); 
  };

  const handleSort = (key, direction) => {
    setOrdering(direction === 'desc' ? `-${key}` : key);
  };

  const handleExportCSV = () => { /* Логика экспорта */ };

  const handleImportCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData(); formData.append('file', file);
    try {
      await API.post('/api/import-excel/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      alert('Вопросы успешно загружены!'); window.location.reload(); 
    } catch (err) { alert('Ошибка при импорте файла.'); }
    e.target.value = null; 
  };

  // ФУНКЦИИ ДЛЯ ЧЕКБОКСОВ
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(questions.map(q => q.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const handleBulkAction = async (actionStr) => {
    if (!window.confirm(`Вы собираетесь применить это действие к ${selectedIds.length} вопросам. Продолжить?`)) return;
    
    try {
      await API.post('/api/questions/bulk-action/', { action: actionStr, ids: selectedIds });
      setSelectedIds([]);
      fetchBankData();
      alert("Действие успешно применено!");
    } catch (err) {
      alert("Ошибка при массовом редактировании.");
      console.error(err);
    }
  };

  return (
    <div className="w-full space-y-6 pb-20 relative">
      
      {/* ПЛАВАЮЩАЯ ПАНЕЛЬ ДЛЯ МАССОВЫХ ДЕЙСТВИЙ */}
      {selectedIds.length > 0 && (
        <div className="fixed bottom-10 left-1/2 -translate-x-1/2 bg-gray-900 text-white px-6 py-4 rounded-[2rem] shadow-2xl z-50 flex items-center gap-6 animate-slide-up">
          <div className="flex flex-col">
            <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">Выбрано вопросов</span>
            <span className="text-xl font-black text-blue-400 leading-none">{selectedIds.length}</span>
          </div>
          <div className="h-8 w-px bg-gray-700"></div>
          <div className="flex items-center gap-2">
            <button onClick={() => handleBulkAction('approve')} className="bg-green-600 hover:bg-green-500 px-4 py-2 rounded-xl text-xs font-bold transition-colors">Сделать общими</button>
            <button onClick={() => handleBulkAction('hide')} className="bg-gray-700 hover:bg-gray-600 px-4 py-2 rounded-xl text-xs font-bold transition-colors">Сделать личными</button>
            <button onClick={() => handleBulkAction('delete')} className="bg-red-600 hover:bg-red-500 px-4 py-2 rounded-xl text-xs font-bold transition-colors">Удалить</button>
          </div>
          <button onClick={() => setSelectedIds([])} className="ml-2 w-8 h-8 flex items-center justify-center rounded-full bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white transition-colors">✕</button>
        </div>
      )}

      <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-black text-gray-800">Управление базой вопросов</h2>
          <p className="text-gray-500 font-medium text-xs mt-1 uppercase tracking-widest">Централизованный аудит всех тестов</p>
        </div>
        <div className="flex gap-3">
          <input type="file" accept=".xlsx, .xls, .csv" className="hidden" ref={fileInputRef} onChange={handleImportCSV} />
          <button onClick={() => fileInputRef.current.click()} className="bg-white border border-gray-200 text-gray-700 px-5 py-2.5 rounded-xl font-bold text-sm shadow-sm hover:bg-gray-50 transition-colors">📥 Импорт</button>
          <button onClick={handleExportCSV} className="bg-green-600 hover:bg-green-700 text-white px-5 py-2.5 rounded-xl font-bold text-sm shadow-md transition-colors">📤 Экспорт</button>
        </div>
      </div>

      {/* ПАНЕЛЬ ФИЛЬТРОВ EXCEL-STYLE */}
      <div className="bg-white p-5 rounded-[2rem] border border-gray-200 shadow-sm flex flex-col lg:flex-row items-center gap-4">
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3 w-full flex-1">
          <ExcelCategoryFilter label="ID" options={idOptions} selectedValues={filters.id} onChange={handleFilterChange} onSort={handleSort} columnKey="id" />
          <ExcelCategoryFilter label="Текст / Фото" options={textOptions} selectedValues={filters.text} onChange={handleFilterChange} onSort={handleSort} columnKey="text" />
          <ExcelCategoryFilter label="Класс" options={classOptions} selectedValues={filters.class_level} onChange={handleFilterChange} onSort={handleSort} columnKey="class_level" />
          <ExcelCategoryFilter label="Предмет" options={subjectOptions} selectedValues={filters.subject} onChange={handleFilterChange} onSort={handleSort} columnKey="subject" />
          <ExcelCategoryFilter label="Сложность" options={difficultyOptions} selectedValues={filters.difficulty} onChange={handleFilterChange} onSort={handleSort} columnKey="difficulty" />
          <ExcelCategoryFilter label="Тема" options={topicOptions} selectedValues={filters.topic} onChange={handleFilterChange} onSort={handleSort} columnKey="topic" />
          <ExcelCategoryFilter label="Автор" options={authorOptions} selectedValues={filters.author} onChange={handleFilterChange} onSort={handleSort} columnKey="creator" />
          <ExcelCategoryFilter label="Статус" options={statusOptions} selectedValues={filters.status} onChange={handleFilterChange} onSort={handleSort} columnKey="status" />
        </div>

        <div className="bg-blue-50 border border-blue-100 px-6 py-2 rounded-2xl shrink-0 text-center flex flex-col justify-center min-w-[120px]">
          <span className="block text-[9px] font-black text-blue-400 uppercase tracking-widest mb-0.5">В базе найдено</span>
          <span className="block text-2xl font-black text-blue-700 leading-none">{loading ? '...' : totalCount}</span>
        </div>
      </div>

      {/* ТАБЛИЦА */}
      <div className="bg-white border border-gray-200 rounded-[2rem] shadow-sm overflow-hidden relative min-h-[400px]">
        {loading && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-sm flex items-center justify-center z-10">
            <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}
        
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm whitespace-nowrap">
            <thead className="bg-gray-50 text-gray-400 font-black uppercase tracking-widest text-[10px] border-b border-gray-100">
              <tr>
                {/* ЧЕКБОКС "ВЫБРАТЬ ВСЕ" */}
                <th className="px-4 py-4 w-10">
                  <input 
                    type="checkbox" 
                    onChange={handleSelectAll} 
                    checked={questions.length > 0 && selectedIds.length === questions.length}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                  />
                </th>
                <th className="px-4 py-4">ID</th>
                <th className="px-6 py-4 w-full">Текст вопроса / Изображение</th>
                <th className="px-6 py-4">Класс</th>
                <th className="px-6 py-4">Предмет</th>
                <th className="px-6 py-4">Сложность</th>
                <th className="px-6 py-4">Тема</th>
                <th className="px-6 py-4">Автор</th>
                <th className="px-6 py-4">Статус</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 font-medium text-gray-700">
              {questions.length === 0 && !loading ? (
                <tr><td colSpan="9" className="px-6 py-12 text-center text-gray-400 font-bold text-sm">Ничего не найдено</td></tr>
              ) : (
                questions.map(q => {
                  const isChecked = selectedIds.includes(q.id);
                  return (
                    <tr key={q.id} className={`transition-colors ${isChecked ? 'bg-blue-50/50' : 'hover:bg-blue-50/20'}`}>
                      <td className="px-4 py-4 align-top pt-5">
                        <input 
                          type="checkbox" 
                          checked={isChecked}
                          onChange={() => handleSelectOne(q.id)}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-4 text-xs font-black text-gray-400 align-top pt-5">#{q.id}</td>
                      <td className="px-6 py-4 align-top pt-5">
                        <div className="flex flex-col gap-3 min-w-[300px] max-w-2xl whitespace-normal break-words">
                          {q.image && <img src={q.image} alt="Вопрос" className="max-h-48 w-auto object-contain bg-gray-50 border border-gray-200 rounded-md shadow-sm p-1 shrink-0" />}
                          <div className="text-sm text-gray-800 overflow-x-auto pb-2">
                            {q.text ? <MathText text={autoFormatMath(q.text)} /> : <span className="text-gray-400 italic font-semibold block">Без текста</span>}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 align-top pt-5"><span className="bg-slate-100 text-slate-600 px-2.5 py-1 rounded-lg text-xs font-black">{q.class_level} класс</span></td>
                      <td className="px-6 py-4 align-top pt-5"><span className="block font-black text-gray-800 text-xs">{getSubjName(q.subject)}</span></td>
                      <td className="px-6 py-4 align-top pt-5"><span className="block text-xs font-bold text-gray-600">{q.difficulty || 'Базовый'}</span></td>
                      <td className="px-6 py-4 text-xs align-top pt-5"><span className="bg-gray-100 text-gray-600 px-2 py-1 rounded truncate max-w-[150px] inline-block">{getTopicName(q.topic)}</span></td>
                      <td className="px-6 py-4 align-top pt-5"><span className="text-blue-600 bg-blue-50 border border-blue-100 px-2.5 py-1 rounded-lg text-xs font-black">{getAuthorName(q)}</span></td>
                      <td className="px-6 py-4 align-top pt-5">
                        {q.is_public ? <span className="bg-green-50 text-green-600 border border-green-200 px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest">Общий</span> : <span className="bg-purple-50 text-purple-600 border border-purple-200 px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest">Личный</span>}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        
        {/* ПАГИНАЦИЯ */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between bg-gray-50/50">
            <span className="text-xs font-bold text-gray-500">Показано 30 записей на страницу</span>
            <div className="flex items-center gap-4">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1 || loading} className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors shadow-sm">
                ← Назад
              </button>
              <span className="text-sm font-black text-gray-800">
                Стр. {currentPage} <span className="text-gray-400 font-medium">из</span> {totalPages}
              </span>
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages || loading} className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors shadow-sm">
                Вперед →
              </button>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}

export default AnalyticsPage;